<template>
  <w-modal
    v-model="show"
    close-button="Anuluj"
    submit-button="Zapisz"
    size="lg"
    title="Wprowadź dane faktury"
    :close-on-submit="false"
    :hide-on-overlay="false"
    :hide-on-esc="false"
    @submit="save"
    @close="close"
  >
    <div class="row">
      <w-vue-select
        class="col-md-12 form-group"
        ajax
        :model="$schema.group"
        :values="ivoiceGroups"
        @search="searchIvoiceGroup"
      />

      <div class="col-md-12">
        <div class="row">
          <w-input class="col-md-6 form-group" mask="##########" :model="$schema.nip" />

          <w-input class="col-md-6 form-group" :model="$schema.number" />
        </div>
      </div>

      <w-datepicker class="col-md-12 form-group" :model="$schema.date" :disabled="$schema.date.$schema.readOnly" />

      <div class="col-md-12">
        <div class="row">
          <w-input class="col-md-6 form-group" :model="$schema.price" />

          <w-input class="col-md-6 form-group" :model="$schema.priceToPay" />
        </div>
      </div>

      <div class="col-md-12">
        <table v-if="invoicesInGroup.length" class="table table-bordered table-striped w-100 dataTable dtr-inline">
          <thead>
            <tr>
              <th>Nazwa szkoły</th>

              <th>ID wniosku beneficjenta</th>

              <th>Kwota zakwalifikowana do refundacji z faktury</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(invoice, index) in invoicesInGroup" :key="'invoice_in_group_' + index">
              <td>{{ invoice.school }}</td>

              <td>{{ invoice.documentId }}</td>

              <td class="text-right">{{ formatAmount(invoice.priceToPay) }}</td>
            </tr>
          </tbody>

          <tfoot>
            <tr>
              <td><b>Razem</b></td>

              <td>-</td>

              <td class="text-right">
                <b>{{ formatAmount(totalPriceToPay) }}</b>
              </td>
            </tr>

            <tr>
              <td><b>Do wykorzystania pozostało</b></td>

              <td>-</td>

              <td class="text-right">
                <b>{{ formatAmount(priceToPayLeft) }}</b>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <w-input class="col-md-12 form-group" :model="$schema.mdokUid" />

      <w-input class="col-md-12 form-group" :model="$schema.description" />
    </div>
  </w-modal>
</template>

<script>
import { mapState } from 'vuex';

import { formatAmount } from '@/filters/formatAmount';
import { manageMixin } from '@/mixins/manage';
import schema from '@/schemas/invoice.schema';
import { tryParseInt } from '@/utils/number';

// @vuese
// Modal faktury
// @group Document
export default {
  mixins: [manageMixin('invoice', {})],
  schema,
  props: {
    // Maksymalna kwota do wypłaty
    maxToPay: { type: Number, default: 0 },

    // Lista faktur
    invoices: { type: Array }
  },

  data() {
    return {
      invoiceGroup: undefined,
      ivoiceGroups: []
    };
  },

  computed: {
    ...mapState({
      // Dokument dla którego jest edytowana faktura
      document: state => state.documentIndex.data
    }),
    // Inne niż aktualnie edytowana faktury
    otherInvoices() {
      return this.invoices?.filter(invoice => invoice.id !== this.id) || [];
    },
    modelInvoiceValue() {
      return { nip: this.model.nip, number: this.model.number, date: this.model.date };
    },
    invoicesInGroup() {
      return this.invoiceGroup?.invoices?.filter(invoice => invoice.id !== this.id) || [];
    },
    totalPriceToPay() {
      return this.invoicesInGroup.reduce((total, invoice) => total + invoice.priceToPay, 0);
    },
    priceToPayLeft() {
      return this.model.price - this.totalPriceToPay;
    },
    procedureId() {
      return tryParseInt(this.$route.params.procedureId);
    }
  },

  watch: {
    maxToPay() {
      this.refreshExternal();
    },

    otherInvoices() {
      this.refreshExternal();
    },

    'model.group': function(newValue) {
      if (this.invoiceGroup && this.formatInvoiceValue(this.invoiceGroup) == newValue) {
        return;
      }
      const group = this.ivoiceGroups.find(group => group.value === newValue)?.data;
      this.setInvoiceGroup(group);
    },
    modelInvoiceValue(newValue) {
      return this.setInvoiceGroup(newValue);
    }
  },

  mounted() {
    this.refreshExternal();
  },

  methods: {
    formatAmount,
    // @vuese
    // Odświeża zewnętrzne dane do obliczeń w schemie
    refreshExternal() {
      this.$options.external = {
        document: this.document,
        maxToPay: this.maxToPay,
        otherInvoices: this.otherInvoices,
        invoiceId: this.id,
        group: this.invoiceGroup,
        invoicesInGroup: this.invoicesInGroup
      };

      this.$setSchema(schema);
      this.$schema.$reset();

      this.$schema.group.$setData(this.formatInvoiceValue(this.invoiceGroup));
    },

    // @vuese
    // Szukanie grup faktur
    searchIvoiceGroup(search) {
      if (!search) {
        return;
      }
      this.$store
        .dispatch('invoice/searchGroup', {
          query: { documentId: this.document.documentId, search },
          preventLoader: true
        })
        .then(result => {
          this.ivoiceGroups = result.map(value => {
            return { value: this.formatInvoiceValue(value), text: this.formatInvoiceText(value), data: value };
          });
        });
    },

    // @vuese
    // Formatowanie wartości grupy faktur (do `value`)
    formatInvoiceValue(value) {
      if (!value) {
        return undefined;
      }
      return [value.number, value.date, value.nip].filter(Boolean).join(';');
    },

    // @vuese
    // Formatowanie opisu grupy faktur (do `text`)
    formatInvoiceText(value) {
      return `${value.number}, ${value.date}, NIP: ${value.nip}, Kwota: ${formatAmount(value.price)} zł`;
    },

    // @vuese
    // Szukanie grupy faktur z danymi faktur
    findInvoiceGroup(data) {
      if (!(data?.nip && data?.number && data?.date)) {
        this.invoiceGroup = undefined;
        return Promise.resolve();
      }

      return this.$store
        .dispatch('invoice/getGroup', {
          documentId: this.document.documentId,
          nip: data.nip,
          number: data.number,
          date: data.date
        })
        .then(result => {
          if (result?.invoices.some(invoice => invoice.id != this.id)) {
            this.invoiceGroup = result;
          }
          return result;
        });
    },

    // @vuese
    // Ustawienie grupy faktur dla aktualnej faktury
    setInvoiceGroup(data) {
      return this.findInvoiceGroup(data).then(result => {
        if (!result) {
          this.$schema.group.$setData('');
          this.$schema.invoicesInGroup.$setLength(0);
          return;
        }

        if (result) {
          this.$schema.$setData(
            {
              group: this.formatInvoiceValue(this.invoiceGroup),
              invoicesInGroup: this.invoicesInGroup,
              nip: result.nip,
              number: result.number,
              date: result.date,
              price: result.price
            },
            undefined,
            true
          );
        }
      });
    }
  }
};
</script>
